import * as React from "react";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_NAV_TITLE,
  ROUTE_TO_NAV_TITLES_MAP,
  ROUTES,
} from "../../../../Constants/Constants";
import { Box, IconButton, Link, Tooltip } from "@mui/material";
import { ArrowBackOutlined } from "@mui/icons-material";

export default function NavbarBreadcrumbs({
  onBackClick = null,
  showBackButton = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const pathSegments = location.pathname.split("/").filter(Boolean);

  const handleBreadcrumbClick = React.useCallback(
    (item, isLast) => {
      if (item.clickable && item.path && !isLast) {
        navigate(item.path);
      }
    },
    [navigate]
  );

  const handleBackClick = React.useCallback(() => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  }, [navigate, onBackClick]);

  const breadCrumbItems = pathSegments.reduce(
    (acc, segment) => {
      if(pathname !== "/home" && acc.index === 0) {
        const homeNavTitle = ROUTE_TO_NAV_TITLES_MAP[ROUTES.HOME];
        acc.items.unshift(homeNavTitle);
      }

      const nextNavTitle = acc.titlesMap[`/${segment}`] || acc.titlesMap[DEFAULT_NAV_TITLE];
      const nextNavTitleObject = typeof nextNavTitle === "function" ? nextNavTitle(segment) : nextNavTitle;
      
      if(!nextNavTitleObject?.hasOwnProperty(DEFAULT_NAV_TITLE)) {
        acc.items = acc.items.concat(nextNavTitleObject);
      }
      
      acc.titlesMap = nextNavTitleObject;
      acc.index++;
      return acc;
    },
    { titlesMap: ROUTE_TO_NAV_TITLES_MAP, items: [], index: 0 }
  );

  const renderBreadcrumbItem = React.useCallback(
    (item, index) => {
      const isLast = index === breadCrumbItems.items.length - 1;

      const itemContent = (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: isLast ? "primary.main" : "text.secondary",
            fontSize: "1rem",
            fontWeight: 400,
            cursor: isLast ? "default" : "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": !isLast
              ? {
                  color: "primary.main",
                  transform: "translateY(-1px)",
                }
              : {},
          }}
        >
          {item.icon}
          <Typography
            variant={isLast ? "h6" : "body1"}
            component="span"
            sx={{
              fontWeight: isLast ? 700 : 400,
              color: "inherit",
            }}
          >
            {item.label}
          </Typography>
        </Box>
      );
      if(isLast) {
          return itemContent;
      }
      if (!item.clickable) {
        return (
          <Tooltip
            key={index}
            title={item.tooltip || item.label}
            arrow
            placement="bottom"
          >
            {itemContent}
          </Tooltip>
        );
      }

      return (
        <Tooltip
          key={index}
          title={item.tooltip || `Navigate to ${item.label}`}
          arrow
          placement="bottom"
        >
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleBreadcrumbClick(item, isLast);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              color: "text.secondary",
              fontSize: "1rem",
              fontWeight: 400,
              "&:hover": {
                color: "primary.main",
                textDecoration: "none",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {itemContent}
          </Link>
        </Tooltip>
      );
    },
    [breadCrumbItems, handleBreadcrumbClick]
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        px: 2,
        borderRadius: 2,
        backgroundColor: "background.default",
        minHeight: 56,
        gap: 2,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {showBackButton && (
          <Tooltip title="Go back" arrow placement="bottom">
            <IconButton
              onClick={handleBackClick}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ArrowBackOutlined />
            </IconButton>
          </Tooltip>
        )}

        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextRoundedIcon fontSize="small" />}
          sx={{
            "& .MuiBreadcrumbs-separator": {
              mx: 1,
              color: "text.secondary",
            },
            "& .MuiBreadcrumbs-ol": {
              alignItems: "center",
            },
          }}
        >
          {breadCrumbItems.items.map((item, index) =>
            renderBreadcrumbItem(item, index)
          )}
        </Breadcrumbs>
      </Box>
    </Box>
  );
}
